import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import currency from "currency.js";
import Price from "../../common/component/Price";
import RadioCardGroup from "../../common/component/RadioCardGroup";
import { UserContext } from "../../context/UserContext";
import {
  getAmountWithTxnFee,
  getAmountWithoutTxnFee,
  isCostPlusFeesModel,
  isNumeric,
} from "../../common/util";
import {
  applyVariantOptionSizeOverride,
  formatVariantOptionFactor,
  removeVariantOptionSizeOverride,
} from "../../common/variantSizeOverride";

/** The option's own pricing, shown on the Inherit card. */
function formatDefaultPricing(pricing) {
  if (pricing?.amount != null && String(pricing.amount).trim() !== "" && +pricing.amount > 0) {
    return `+${currency(pricing.amount).format()}`;
  }
  if (pricing?.factor != null && String(pricing.factor).trim() !== "" && +pricing.factor > 0) {
    return formatVariantOptionFactor(pricing.factor);
  }
  return "No upcharge";
}

/**
 * Edit per-size override for one option: option.override.size[sizeName].
 */
export default function VariantOptionSizeOverrideCellDialog(props) {
  const { open, onClose, selected, onSubmit } = props;
  const { user } = useContext(UserContext);
  const { option, sizeName, optionLabel } = selected || {};

  const [allowed, setAllowed] = useState(true);
  const [pricingType, setPricingType] = useState("inherit");
  const [price, setPrice] = useState("");
  const [includeFees, setIncludeFees] = useState(
    user.config?.transferFees === "false" ? false : true
  );
  const [error, setError] = useState("");
  const [invalidPrice, setInvalidPrice] = useState(false);

  const clearPrice = () => {
    setPrice("");
    setError("");
    setInvalidPrice(false);
  };

  useEffect(() => {
    if (!open || !selected || !option || !sizeName) return;
    const overrideData = option.override?.size?.[sizeName];
    const feesIncluded = user.config?.transferFees === "false" ? false : true;
    const nextAllowed = overrideData == null ? true : String(overrideData.allow) !== "false";
    const hasStoredAmount =
      overrideData?.amount != null && String(overrideData.amount).trim() !== "";
    setAllowed(nextAllowed);
    setPricingType(nextAllowed && hasStoredAmount ? "upcharge" : "inherit");
    setIncludeFees(feesIncluded);
    if (hasStoredAmount) {
      const stored = overrideData.amount;
      setPrice(
        isCostPlusFeesModel(user.config?.fees) && feesIncluded
          ? getAmountWithoutTxnFee(stored, user.config?.fees)
          : stored
      );
    } else {
      setPrice("");
    }
    setError("");
    setInvalidPrice(false);
  }, [open, selected, option, sizeName, user.config?.fees, user.config?.transferFees]);

  const handleClose = () => {
    setError("");
    setInvalidPrice(false);
    onClose?.();
  };

  /** Do not use a nested <form> here — VariantFieldEditorDialog wraps content in a parent <form>, and a submit button would submit that outer form and close it without saving the cell override. */
  const saveOverride = () => {
    const requiresUpcharge = allowed && pricingType === "upcharge";
    const trimmed = requiresUpcharge ? String(price || "").trim() : "";
    if (requiresUpcharge && (invalidPrice || !trimmed || !isNumeric(+trimmed))) {
      setError("Enter a valid number");
      return;
    }

    // Inherit stores no amount so the option's own pricing applies to this size.
    let storedAmount = "";
    if (trimmed) {
      const totalPrice =
        isCostPlusFeesModel(user.config?.fees) && includeFees
          ? getAmountWithTxnFee(price, user.config?.fees)
          : price;
      storedAmount = String(currency(totalPrice).value);
    }

    const nextOption = applyVariantOptionSizeOverride(option, sizeName, {
      allowed,
      amount: storedAmount,
    });
    onSubmit?.(nextOption);
    handleClose();
  };

  const deleteOverride = () => {
    if (!option || !sizeName) return;
    onSubmit?.(removeVariantOptionSizeOverride(option, sizeName));
    handleClose();
  };

  const optionName = optionLabel || option?.name || "";
  const hasExistingOverride = Boolean(option?.override?.size?.[sizeName]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Edit Override
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            saveOverride();
          }
        }}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Box>
            <Typography variant="overline" color="text.secondary" display="block">
              Option
            </Typography>
            <Typography variant="body1">{optionName || "—"}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary" display="block">
              Size
            </Typography>
            <Typography variant="body1">{sizeName || "—"}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Availability
            </Typography>
            <RadioCardGroup
              name="availability"
              direction="row"
              value={allowed ? "available" : "unavailable"}
              onChange={(e) => {
                const next = e.target.value === "available";
                setAllowed(next);
                if (!next) {
                  setPricingType("inherit");
                  clearPrice();
                }
              }}
              options={[
                { value: "available", title: "Available" },
                { value: "unavailable", title: "Not Available" },
              ]}
            />
          </Box>
          {allowed && (
            <Box>
              <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Pricing
              </Typography>
              <RadioCardGroup
                name="pricing"
                direction="row"
                value={pricingType}
                onChange={(e) => {
                  const next = e.target.value;
                  setPricingType(next);
                  if (next !== "upcharge") clearPrice();
                }}
                options={[
                  {
                    value: "inherit",
                    title: "Inherit",
                    description: formatDefaultPricing(option?.pricing),
                  },
                  { value: "upcharge", title: "Upcharge" },
                ]}
              />
            </Box>
          )}
          {allowed && pricingType === "upcharge" && (
            <Box sx={{ mt: 1 }}>
              <Price
                label="Upcharge Amount"
                required
                value={{ amount: price, includeFees }}
                onChange={(o) => {
                  setPrice(o.amount);
                  if (typeof o.includeFees === "boolean") setIncludeFees(o.includeFees);
                  setInvalidPrice(Boolean(o.badInput));
                  setError("");
                }}
              />
            </Box>
          )}
          {error ? (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ gap: 1 }}>
        {hasExistingOverride && (
          <Button
            type="button"
            color="error"
            variant="outlined"
            fullWidth
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              deleteOverride();
            }}
          >
            Delete
          </Button>
        )}
        <Button
          type="button"
          color="primary"
          variant="contained"
          fullWidth
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            saveOverride();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
